import {
  fetchProjectFromSDK,
  fetchPublicProjectFromSDK,
  unlockPublicProjectFromSDK,
  resolveEnvPublishConfig
} from './sdk.js'
import { resolveProjectParams } from './resolveProjectParams.js'
import { loadPublicPreviewData } from './publicPreviewData.js'

// Pull the HTTP status code out of an SDK/fetch error so callers can render
// the real error (401, 403, 500, …) instead of collapsing everything to 404.
// Checks the common shapes first (err.status, err.statusCode, err.response)
// and falls back to scanning the message for a 4xx/5xx number.
export function resolveFetchErrorStatus(err) {
  if (!err) return null
  if (typeof err.status === 'number') return err.status
  if (typeof err.statusCode === 'number') return err.statusCode
  if (typeof err.response?.status === 'number') return err.response.status
  if (typeof err.cause?.status === 'number') return err.cause.status
  const msg = (err.message || '') + ' ' + (err.cause?.message || '')
  const match = msg.match(/\b([4-5]\d{2})\b/)
  return match ? parseInt(match[1], 10) : null
}

// FA204 / frank trap — TRUE_VALUES/FALSE_VALUES/parseFlag/withTimeout/
// `params`/`resolved`/_hasSdkAccessToken used to live at module scope,
// referenced bare from the two exports below (project_frank_drops_module_
// local_helpers). Each export now carries its own self-contained inline
// copy. `params`/`resolved` used to be computed ONCE at module load —
// recomputing them fresh inside each call reads the CURRENT
// `window.location.search` / URL, which is at least as correct in an SPA
// (the location can change between calls) and behaviorally identical for
// the cold-load case these two exports actually run in.

// All seven params below take their fallback in the BODY, not as
// destructuring defaults — frank's `to-json` rewrites a destructuring
// default that references a free var into `__scope.<name>` correctly
// value-wise, but the rewritten TEXT differs from source, which trips the
// `functions` arity-contract check as `signature-drift` even though arity
// itself never changes (same class as authGuard.js's DEFAULT_AUTH_PATHS —
// see there for detail). A body reassignment doesn't touch the signature.
export async function getProjectData({
  owner,
  key,
  branch,
  useLocal,
  // Opt-in only — when no `?useNext=` URL flag is present the SDK falls
  // through to its channel-based resolution (workspaceApiBase →
  // hostname autodetect / build NODE_ENV / defaultChannel). Previously
  // this defaulted to true via `parseFlag(...) !== false`, forcing every
  // preview fetch through next.api.symbols.app even when the host was
  // dev.preview.symbols.app or local.
  useNext,
  env,
  version,
  // USER-CODE-ORIGIN security review B3: the payload UNREVIVED (function
  // fields stay strings) for a caller that must never evaluate project code
  // — the in-app preview; the opaque user-code frame revives it.
  raw = false
} = {}) {
  const TRUE_VALUES = new Set(['true', '1', 'on', 'yes'])
  const FALSE_VALUES = new Set(['false', '0', 'off', 'no'])
  function parseFlag(v) {
    if (v === true) return true
    if (v === false) return false
    if (v == null) return null
    const lower = String(v).toLowerCase()
    if (TRUE_VALUES.has(lower)) return true
    if (FALSE_VALUES.has(lower)) return false
    return null
  }
  // 3s timeout matches the SDK's own /me budget — if the env config
  // resolver takes longer than that, fall through to the env-name-as-
  // branch path so the user still gets a render attempt.
  const withTimeout = (p, ms = 3000) => {
    let id
    return Promise.race([
      p,
      new Promise((r) => {
        id = setTimeout(() => r(null), ms)
      })
    ]).finally(() => clearTimeout(id))
  }
  const params = new URLSearchParams(window.location.search)
  const resolved = resolveProjectParams()
  owner = owner ?? resolved.owner
  key = key ?? resolved.key
  branch = branch ?? (resolved.branch || 'main')
  useLocal = useLocal ?? parseFlag(params.get('useLocal')) === true
  useNext = useNext ?? parseFlag(params.get('useNext')) === true
  env = env ?? resolved.env
  version = version ?? resolved.version

  if (!owner || !key) throw new Error('Provide both owner and key')

  const projectKey = `${owner}/${key}`
  const sdkBase = { useLocal, useNext, ...(raw === true ? { raw: true } : {}) }

  // Preview domains serve CONSUMPTION, public only (PREVIEW-HOST-PUBLIC-ONLY,
  // Nika 2026-10-09). The host never holds a session, so it never touches the
  // editor's authed data route, never probes visibility, and never resolves an
  // env config (`resolveEnvPublishConfig` is an authed read) — it asks the
  // anonymous public route and nothing else. A private project and a missing
  // one end in the SAME `project_unavailable` error (./publicPreviewData.js).
  //
  // Matches production (`*.preview.symbols.app`) AND local dev
  // (`preview.localhost`, `*.preview.localhost`) so the `preview.` subdomain
  // convention stays consistent across environments.
  const host = window.location.hostname
  const isPreviewHost =
    host.endsWith('.preview.symbols.app') ||
    host === 'preview.localhost' ||
    host.endsWith('.preview.localhost')
  if (isPreviewHost) {
    return loadPublicPreviewData({
      owner,
      key,
      env,
      fetchPublic: (envKey) => fetchPublicProjectFromSDK(owner, key, { envKey, ...sdkBase })
    })
  }

  // `env` maps to one of the project's configured environments
  // (listEnvironments → each with its own mode/branch/version). When no env
  // config is found, fall back to treating the env name as a branch.
  let envOpts = { branch, version: version || 'latest' }
  if (env) {
    const envResolved = await withTimeout(
      resolveEnvPublishConfig(projectKey, env, sdkBase).catch(() => null)
    )
    envOpts = envResolved || { branch: env, version: 'latest' }
    // An explicit version pin (version-history preview) wins over the env's
    // published config — the inner resolution used to shadow `resolved` and
    // silently discard `?version=` whenever `?env=` was present.
    if (version) envOpts = { ...envOpts, version }
  }

  const data = await fetchProjectFromSDK(owner, key, { ...sdkBase, ...envOpts })
  if (data) return data
  throw new Error(`No data returned for project "${projectKey}"`)
}

// Unlock a password-protected preview. `password` is the SHA-256 hex of
// the user-typed value (matches how project-account.js stores it).
// Returns the same shape as `getProjectData` on success so the preview
// can swap state.data + re-render. Returns null on invalid password —
// caller re-prompts.
// Same body-fallback rationale as `getProjectData` above.
export async function unlockProjectData({
  owner,
  key,
  envKey,
  password,
  useLocal,
  // Same channel-first default as `getProjectData` above — opt-in only.
  useNext,
  // see getProjectData (security review B3)
  raw = false
} = {}) {
  // FA204 / frank trap — self-contained inline copy, see getProjectData above.
  const TRUE_VALUES = new Set(['true', '1', 'on', 'yes'])
  const FALSE_VALUES = new Set(['false', '0', 'off', 'no'])
  function parseFlag(v) {
    if (v === true) return true
    if (v === false) return false
    if (v == null) return null
    const lower = String(v).toLowerCase()
    if (TRUE_VALUES.has(lower)) return true
    if (FALSE_VALUES.has(lower)) return false
    return null
  }
  const params = new URLSearchParams(window.location.search)
  const resolved = resolveProjectParams()

  owner = owner ?? resolved.owner
  key = key ?? resolved.key
  useLocal = useLocal ?? parseFlag(params.get('useLocal')) === true
  useNext = useNext ?? parseFlag(params.get('useNext')) === true

  if (!owner || !key) throw new Error('Provide both owner and key')
  if (!envKey) throw new Error('envKey required')
  if (!password) throw new Error('password required')
  const data = await unlockPublicProjectFromSDK(owner, key, {
    envKey,
    password,
    useLocal,
    useNext,
    ...(raw === true ? { raw: true } : {})
  })
  if (!data) return null
  return {
    ...data,
    _resolvedEnv: envKey,
    _requestedEnv: envKey,
    _usedFallback: false,
    _unlocked: true
  }
}
